import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import CertificationsService from '@/services/CertificationsService'
import MojeProjektyService from '@/services/MojeProjektyService'

const formatDate = value => (value ? `${value}`.substring(0, 10) : '—')

const resolveCertificationNames = (step, certificationMap) => {
	const certificates = Array.isArray(step?.certificates) ? step.certificates : []
	const ids = Array.isArray(step?.requiredCertificationIds) && step.requiredCertificationIds.length
		? step.requiredCertificationIds
		: certificates.map(item => item.certificateId)

	return (Array.isArray(ids) ? ids : [])
		.map(id => {
			const key = `${id ?? ''}`.trim()
			if (!key) return ''
			const stored = certificates.find(item => `${item.certificateId}` === key)
			const storedName = `${stored?.certificateName || ''}`.trim()
			if (storedName) return storedName
			return certificationMap.get(key) || ''
		})
		.filter(Boolean)
}

export default function useMyProjectDetailsPage() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()
	const [project, setProject] = useState(null)
	const [certificationOptions, setCertificationOptions] = useState([])
	const [loading, setLoading] = useState(true)

	const loadProject = useCallback(async () => {
		setLoading(true)
		const [result, certificationsResult] = await Promise.all([
			MojeProjektyService.getById(id),
			CertificationsService.getAll({ limit: 999, sortColumn: 'name', sortDirection: 'asc' }),
		])

		if (result.error || !result.data) {
			await Swal.fire(
				t('notifications.errorTitle'),
				result.error || t('myProjects.details.messages.loadFailed', { defaultValue: 'Nie udało się wczytać projektu' }),
				'error'
			)
			navigate('/centrumSzkolen/moje-projekty')
			return
		}

		setProject(result.data)
		setCertificationOptions(
			(Array.isArray(certificationsResult.data) ? certificationsResult.data : []).map(item => ({
				value: `${item._id || item.id || ''}`.trim(),
				label: item.name || '—',
			})).filter(item => item.value)
		)
		setLoading(false)
	}, [id, navigate, t])

	useEffect(() => {
		if (id) loadProject()
	}, [id, loadProject])

	const refreshProject = useCallback(async () => {
		const result = await MojeProjektyService.getById(id)
		if (!result.error && result.data) {
			setProject(result.data)
		}
	}, [id])

	const statusLabel = useMemo(() => {
		if (!project?.status) return '—'
		return t(`projects.list.statusOptions.${project.status}`, { defaultValue: project.status })
	}, [project?.status, t])

	const certificationMap = useMemo(
		() => new Map(
			(Array.isArray(certificationOptions) ? certificationOptions : []).map(item => [
				`${item.value}`,
				item.label || '—',
			])
		),
		[certificationOptions]
	)

	const mySteps = useMemo(() => {
		const steps = Array.isArray(project?.steps) ? project.steps : []

		return steps.map(step => {
			const pozycje = Array.isArray(step.pozycje) ? step.pozycje : []
			return {
				...step,
				pozycje,
				trainerNames: pozycje.map(item => item.trainerName).filter(Boolean),
				certificationNames: resolveCertificationNames(step, certificationMap),
			}
		})
	}, [certificationMap, project?.steps])

	const uploadProjectFile = useCallback(async (file, onUploadProgress) => {
		const result = await MojeProjektyService.uploadProjectFile(id, file, onUploadProgress)
		if (result.success) {
			await refreshProject()
		}
		return result
	}, [id, refreshProject])

	const downloadProjectFile = useCallback(
		fileUuid => MojeProjektyService.downloadProjectFile(id, fileUuid),
		[id]
	)

	const deleteProjectFile = useCallback(async fileUuid => {
		const result = await MojeProjektyService.deleteProjectFile(id, fileUuid)
		if (result.success) {
			await refreshProject()
		}
		return result
	}, [id, refreshProject])

	return {
		project,
		loading,
		statusLabel,
		createdDate: formatDate(project?.created),
		mySteps,
		uploadProjectFile,
		downloadProjectFile,
		deleteProjectFile,
	}
}
